import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Zap, Target, PenTool,
  Code, Globe, Cpu, Layers, Box, Terminal,
  Activity, Shield, FileText, Share2,
  Monitor, Smartphone, Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import VictoryCelebration from './VictoryCelebration';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Engineering Map V1.0 - Coordinates
const NODES = [
  // Core Layer (Foundation)
  { id: "first_doc_ai", x: 15, y: 50, icon: FileText, label: "DOC_IA_V1", description: "Primeiro Documento IA" },
  { id: "deep_research", x: 25, y: 30, icon: Search, label: "PESQ_PROFUN", description: "Pesquisa Profunda de IA" },
  { id: "prompt_architect", x: 25, y: 70, icon: Terminal, label: "ARQ_PROMPT", description: "Arquitetura de Prompt" },

  // Creative Layer (Production)
  { id: "image_gen", x: 40, y: 20, icon: PenTool, label: "GERA_IMAGEM", description: "Síntese de Imagem" },
  { id: "video_gen", x: 40, y: 50, icon: Monitor, label: "RENDER_VID", description: "Renderização de Vídeo" },
  { id: "presentation_gen", x: 40, y: 80, icon: Share2, label: "AUTO_APRES", description: "Automação de PPT" },

  // Engineering Layer (Build)
  { id: "site_gen", x: 60, y: 25, icon: Globe, label: "WEB_DEPLOY", description: "Deploy Web Automatizado" },
  { id: "automation_gen", x: 60, y: 50, icon: Zap, label: "FLUXO_AUTO", description: "Automação de Fluxos" },
  { id: "api_usage", x: 60, y: 75, icon: Code, label: "INTEGRA_API", description: "Integração via API" },

  // Agentic Layer (Intelligence)
  { id: "personal_agent", x: 75, y: 35, icon: Smartphone, label: "AGENTE_PESS", description: "Agente Inteligente Pessoal" },
  { id: "multi_agent", x: 75, y: 65, icon: Layers, label: "REDE_SWARM", description: "Inteligência de Enxame" },
  { id: "tool_master", x: 50, y: 5, icon: Box, label: "MASTER_TOOLS", description: "Domínio de Ferramentas IA" },

  // Strategy Layer (Business)
  { id: "biz_ai_plan", x: 85, y: 20, icon: Target, label: "ESTRAT_NEG", description: "Estratégia de Negócios" },
  { id: "ai_first_area", x: 90, y: 50, icon: Activity, label: "TRANSF_DEPT", description: "Transformação de Área" },
  { id: "ai_first_biz", x: 85, y: 80, icon: Trophy, label: "TRANSF_CORP", description: "Transformação Corporativa" },

  // Advanced
  { id: "pro_efficiency", x: 95, y: 95, icon: Shield, label: "MAX_EFICIENC", description: "Eficiência Máxima" },
];

const CONNECTIONS = [
  ["first_doc_ai", "deep_research"],
  ["first_doc_ai", "prompt_architect"],
  ["deep_research", "image_gen"],
  ["prompt_architect", "presentation_gen"],
  ["image_gen", "video_gen"],
  ["video_gen", "site_gen"],
  ["presentation_gen", "site_gen"],
  ["site_gen", "automation_gen"],
  ["automation_gen", "api_usage"],
  ["automation_gen", "personal_agent"],
  ["personal_agent", "multi_agent"],
  ["multi_agent", "ai_first_biz"],
  ["biz_ai_plan", "ai_first_area"],
  ["ai_first_area", "ai_first_biz"]
];

const NeuralNode = React.memo(({ node, isSelected, onClick }) => {
  const Icon = node.icon;
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            className="absolute -translate-x-1/2 -translate-y-1/2 z-20 cursor-pointer"
            style={{ left: `${node.x}%`, top: `${node.y}%` }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.2 }}
            onClick={() => onClick(node)}
          >
            <div className={cn(
              "absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap font-mono tracking-tighter uppercase transition-colors px-1 bg-aift-graphite border",
              node.unlocked ? "text-aift-orange border-aift-orange/50" : "text-muted-foreground border-transparent"
            )}>
              {node.label}
            </div>

            <motion.div
              className={cn(
                "relative flex items-center justify-center transition-all duration-300 border-2",
                node.unlocked
                  ? "w-8 h-8 bg-aift-graphite border-aift-orange text-aift-orange shadow-[0_0_15px_rgba(249,115,22,0.4)]"
                  : "w-6 h-6 bg-aift-graphite border-muted-foreground/30 text-muted-foreground"
              )}
            >
              <Icon className={cn(
                "w-4 h-4 transition-all duration-300",
                node.unlocked ? "opacity-100" : "opacity-50 grayscale"
              )} />
            </motion.div>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-aift-graphite border border-aift-orange/30 text-aift-orange font-mono text-xs shadow-lg shadow-black/50 z-50">
          <p>{node.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

const NeuralEdge = React.memo(({ line, index }) => {
  return (
    <motion.path
      d={`M ${line.x1}% ${line.y1}% L ${line.x2}% ${line.y2}%`}
      fill="none"
      strokeWidth={line.active ? 2 : 1}
      stroke={line.active ? "#F97316" : "#333"}
      strokeDasharray={line.active ? "0" : "5 5"}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{ duration: 1.2, delay: index * 0.03 }}
    />
  );
});

export default function GamificationNeuralGrid({ achievements = [] }) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [celebrationAchievement, setCelebrationAchievement] = useState(null);

  // Check achievement status
  const nodeStatus = useMemo(() => {
    return NODES.map(node => {
      const isUnlocked = achievements.some(a => a.id === node.id);
      return { ...node, unlocked: isUnlocked };
    });
  }, [achievements]);

  // Determine active connections (both nodes unlocked)
  const edges = useMemo(() => {
    return CONNECTIONS.map(([startId, endId]) => {
      const startNode = NODES.find(n => n.id === startId);
      const endNode = NODES.find(n => n.id === endId);
      const isStartUnlocked = achievements.some(a => a.id === startId);
      const isEndUnlocked = achievements.some(a => a.id === endId);

      return {
        x1: startNode.x, y1: startNode.y,
        x2: endNode.x, y2: endNode.y,
        active: isStartUnlocked && isEndUnlocked
      };
    });
  }, [achievements]);

  const handleNodeClick = (node) => {
    if (node.unlocked) {
      // Play sound or effect?
      const achievementData = achievements.find(a => a.id === node.id);
      if (achievementData) {
        setCelebrationAchievement({
          ...achievementData,
          icon: node.icon
        });
      }
    }
    setSelectedNode(node);
  };

  return (
    <div className="relative w-full h-[500px] md:h-[600px] my-10 select-none group bg-aift-graphite rounded-none overflow-hidden border border-white/10 shadow-none">

      {/* Engineering Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

      {/* Scanline Effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-aift-orange/5 to-transparent h-[20%] w-full animate-scanline pointer-events-none z-0" />

      {/* Radial Gradient Focus */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-aift-blue/5 blur-[100px] rounded-full" />

      <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
        {edges.map((line, i) => (
          <NeuralEdge key={i} line={line} index={i} />
        ))}
      </svg>

      {nodeStatus.map((node) => (
        <NeuralNode
          key={node.id}
          node={node}
          isSelected={selectedNode?.id === node.id}
          onClick={handleNodeClick}
        />
      ))}

      {/* Tech HUD Corner */}
      <div className="absolute top-4 right-4 z-30">
        <div className="tech-card p-2 border border-aift-orange/30 bg-aift-graphite/90 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-aift-orange animate-pulse" />
            <span className="text-xs font-mono text-aift-orange">GRID_ATIVO</span>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {celebrationAchievement && (
          <VictoryCelebration
            achievement={celebrationAchievement}
            onClose={() => setCelebrationAchievement(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
